/**
 * Bounty browsing page mounted at "/bounties/*" (see app/App.tsx).
 * Renders a category/sex/category-type filter sidebar (from types/category) that
 * drives ?category, ?categoryType and ?sex query params, plus nested routes:
 * "/bounties" -> ItemCollection grid of active bounties, and
 * "/bounties/:bountyId" -> BountyViewSubmit detail page.
 * Sidebar collapses to a slide-in drawer on mobile.
 */
import React, { useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { Categories, Category, Sex, FullCategories, Sexes } from "@/types/category";
import ItemCollection from "@/components/ItemCollection";
import BountyViewSubmit from "@/pages/bounty/BountyViewSubmit";
import { Menu, X } from "lucide-react";

type TopLevelComponent = "all categories";
const sidebarArrays: [TopLevelComponent, readonly Category[]][] = [["all categories", Categories]];

export const BrowseCollection: React.FC = () => {
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const selectedCategory: Category | null = (queryParams.get("category") as Category) || null;
    const selectedCategoryType: string | null = queryParams.get("categoryType") || null;
    const selectedSex: Sex | null = (queryParams.get("sex") as Sex) || null;
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    function handleCategoryClick(category: Category | null) {
        let path = "/bounties";
        if (category) {
            path += `?category=${category}`;
        }
        navigate(path);
    }

    function handleSexClick(sex: Sex | null) {
        let path = "/bounties?category=" + selectedCategory;
        if (sex) {
            path += `&sex=${sex}`;
        }
        navigate(path);
    }

    function handleCategoryTypeClick(categoryType: string | null) {
        let path = "/bounties?category=" + selectedCategory;
        if (categoryType) {
            path += `&categoryType=${categoryType}`;
        }
        if (selectedSex) {
            path += `&sex=${selectedSex}`;
        }
        navigate(path);
    }

    const renderSidebarMenu: React.FC = () => {
        return (
            <div className="w-full h-full flex flex-col space-y-4 overflow-auto overflow-x-hidden">
                {sidebarArrays.map(([topLevel, categories]) => {
                    return (
                        <React.Fragment key={topLevel}>
                            <button
                                onClick={() => handleCategoryClick(null)}
                                className={`w-full text-xl font-porsui text-left break-words ${
                                    selectedCategory === null ? "font-bold" : "font-porsui"
                                }`}
                            >
                                {topLevel}
                            </button>
                            <div className="w-full flex flex-col space-y-4 overflow-x-hidden">
                                {categories.map((category) => (
                                    <div key={category} className="flex flex-col overflow-x-hidden">
                                        <button
                                            onClick={() => handleCategoryClick(category)}
                                            className={`w-full text-left ml-4 mb-2 font-porsui break-words ${
                                                category === selectedCategory
                                                    ? "bg-slate-50 font-bold"
                                                    : "font-porsui"
                                            }`}
                                        >
                                            {category}
                                        </button>
                                        {selectedCategory === category && (
                                            <div className="w-full flex flex-col space-y-2 ml-4 overflow-x-hidden">
                                                {Array.isArray(FullCategories[category])
                                                    ? (FullCategories[category] as string[]).map(
                                                          (type) => (
                                                              <button
                                                                  key={type}
                                                                  className={`text-sm text-left font-porsui p-1 pl-2 ${
                                                                      selectedSex === type ||
                                                                      selectedCategoryType === type
                                                                          ? "font-bold"
                                                                          : "font-porsui"
                                                                  }`}
                                                                  onClick={() => {
                                                                      if (
                                                                          Sexes.includes(
                                                                              type as Sex
                                                                          )
                                                                      ) {
                                                                          handleSexClick(
                                                                              type as Sex
                                                                          );
                                                                      } else {
                                                                          handleCategoryTypeClick(
                                                                              type
                                                                          );
                                                                      }
                                                                  }}
                                                              >
                                                                  {type}
                                                              </button>
                                                          )
                                                      )
                                                    : !selectedSex
                                                    ? Object.entries(
                                                          FullCategories[category] as {
                                                              [key in Sex]: readonly string[];
                                                          }
                                                      ).map(([sex, _]) => (
                                                          <div key={sex} className="flex flex-col">
                                                              <button
                                                                  key={sex}
                                                                  className={`text-sm text-left p-1 pl-2 ${
                                                                      selectedSex === sex
                                                                          ? "font-bold"
                                                                          : "font-porsui"
                                                                  }`}
                                                                  onClick={() =>
                                                                      handleSexClick(sex as Sex)
                                                                  }
                                                              >
                                                                  {sex}
                                                              </button>
                                                          </div>
                                                      ))
                                                    : Object.entries(
                                                          FullCategories[category] as {
                                                              [key in Sex]: readonly string[];
                                                          }
                                                      ).map(([sex, types]) => (
                                                          <div key={sex} className="flex flex-col">
                                                              <button
                                                                  key={sex}
                                                                  className={`text-sm text-left p-1 pl-2 ${
                                                                      selectedSex === sex
                                                                          ? "font-bold"
                                                                          : "font-porsui"
                                                                  }`}
                                                                  onClick={() =>
                                                                      handleSexClick(sex as Sex)
                                                                  }
                                                              >
                                                                  {sex}
                                                              </button>
                                                              {selectedSex === sex && (
                                                                  <div className="flex flex-col p-1 pl-2 space-y-1">
                                                                      {types.map((type) => (
                                                                          <button
                                                                              key={type}
                                                                              className={`text-sm text-left p-1 pl-2 ${
                                                                                  selectedCategoryType ===
                                                                                  type
                                                                                      ? "font-bold"
                                                                                      : "font-porsui"
                                                                              }`}
                                                                              onClick={() =>
                                                                                  handleCategoryTypeClick(
                                                                                      type
                                                                                  )
                                                                              }
                                                                          >
                                                                              {type}
                                                                          </button>
                                                                      ))}
                                                                  </div>
                                                              )}
                                                          </div>
                                                      ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="bg-slate-50 h-full w-full flex text-black font-porsui relative">
            {/* Mobile Menu Button - Only visible on mobile when sidebar is closed */}
            <button
                onClick={() => setIsSidebarOpen(true)}
                className={`md:hidden fixed left-0 top-[4.5rem] z-30 bg-white p-2 rounded-r-lg shadow-md hover:bg-slate-100 transition-opacity duration-300 ${
                    isSidebarOpen ? "opacity-0" : "opacity-100"
                }`}
            >
                <Menu className="w-6 h-6" />
            </button>

            {/* Sidebar */}
            <div
                className={`
                fixed md:relative top-16 left-0 h-[calc(100%-4rem)] bg-white z-20
                transform transition-transform duration-300 ease-in-out
                md:transform-none md:top-0 md:h-full
                ${isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
                w-full max-w-64 md:w-64 flex flex-col overflow-x-hidden shadow-[4px_0_8px_rgba(0,0,0,0.1)]
            `}
            >
                {/* Close button - only visible on mobile */}
                <button
                    onClick={() => setIsSidebarOpen(false)}
                    className="md:hidden absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full z-30"
                >
                    <X className="w-6 h-6" />
                </button>

                <div className="p-8 h-full overflow-auto overflow-x-hidden bg-white shadow-lg">
                    {renderSidebarMenu({})}
                </div>
            </div>

            {/* Overlay - only visible on mobile when sidebar is open */}
            {isSidebarOpen && (
                <div
                    className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Main Content */}
            <div className="flex-1 overflow-auto ml-0 md:ml-2 relative z-0">
                <Routes>
                    <Route
                        path="/"
                        element={
                            <ItemCollection
                                type="browse"
                                itemType="bounty"
                                statusType="active"
                                format="grid"
                            />
                        }
                    />
                    <Route path=":bountyId" element={<BountyViewSubmit />} />
                </Routes>
            </div>
        </div>
    );
};
